import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, User, Shield, Bell, Settings, LayoutDashboard } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/platform/useProfile";

const AdminHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { data: profileData } = useProfile();
  const profile = profileData?.profile;

  useEffect(() => {
    checkAuthStatus();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAuthStatus();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkAuthStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      setIsLoggedIn(true);
      setUserEmail(user.email);
      
      // Fetch user role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .single();
      
      if (roleData) {
        setUserRole(roleData.role);
      }
    } else {
      setIsLoggedIn(false);
      setUserRole(null);
      setUserEmail(null);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    navigate("/admin-auth");
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes("creator-management")) return "Creator Management";
    if (path.includes("venue-oversight")) return "Venue Oversight";
    if (path.includes("platform-control")) return "Platform Control";
    if (path.includes("landing-page-2")) return "Agency Dashboard";
    return "MCG Agency";
  };

  const getRoleBadgeColor = (role: string | null) => {
    switch (role) {
      case "admin":
        return "bg-gradient-to-r from-white to-gray-300 text-black";
      case "platform_control":
        return "bg-gradient-to-r from-gray-300 to-gray-400 text-black";
      case "creator_manager":
        return "bg-gradient-to-r from-gray-200 to-gray-300 text-black";
      default:
        return "bg-gradient-to-r from-gray-800 to-black text-white";
    }
  };

  return (
    <header className="fixed top-0 w-full bg-black z-50 border-b border-white/20">
      <div className="container mx-auto px-6 py-3">
        <div className="grid grid-cols-3 items-center">
          {/* Left: Same pages that appear in the first landing page */}
          <div className="flex items-center gap-2 justify-start">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10 md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
            <Button variant="ghost" className="text-white hover:bg-white/10 hidden md:flex" asChild>
              <Link to="/events">Events</Link>
            </Button>
            <Button variant="ghost" className="text-white hover:bg-white/10 hidden md:flex" asChild>
              <Link to="/pricing">Pricing</Link>
            </Button>

            {!isLoggedIn && <Button className="hidden md:flex" asChild>
              <Link to="/auth">Sign In</Link>
            </Button>}

            {userRole && (
              <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${getRoleBadgeColor(userRole)}`}>
                <Shield className="w-3 h-3" />
                {userRole === "admin" ? "Administrator" : 
                 userRole === "platform_control" ? "Platform Control" : 
                 userRole.charAt(0).toUpperCase() + userRole.slice(1)}
              </div>
            )}
          </div>

          {/* Center Logo */}
          <Link 
            to={isLoggedIn ? "/landing-page-2" : "/"}
            className="flex flex-col items-center space-y-1 hover:opacity-80 transition-opacity justify-self-center text-center"
          >
            <img
              src="/lovable-uploads/metro-city-groove-logo-white.png"
              alt="Metro City Groove Logo"
              className="w-16 h-16 object-contain drop-shadow-[0_0_12px_rgba(255,255,255,0.25)]"
            />
            <h1 className="text-sm sm:text-lg font-bold text-white text-center whitespace-nowrap">
              Metro City Groove
            </h1>
          </Link>

          {/* Right: Desktop Navigation & Mobile Menu */}
          <div className="flex items-center justify-end gap-4">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10"
                asChild
              >
                <Link to="/agency-pricing">Consultation</Link>
              </Button>
              {isLoggedIn ? (
                <>
                  <Link 
                    to="/landing-page-2"
                    className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/5"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span>Dashboard</span>
                  </Link>
                  
                  <div className="relative group">
                     <button className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/5" aria-label="Account menu">
                       <Avatar className="h-7 w-7 border border-border"><AvatarImage src={profile?.avatar_url ?? undefined} alt="Your profile picture" /><AvatarFallback><User className="w-4 h-4" /></AvatarFallback></Avatar>
                      <span className="max-w-[120px] truncate">{userEmail?.split('@')[0] || "Admin"}</span>
                    </button>
                    <div className="absolute right-0 top-full mt-2 w-48 bg-gradient-to-b from-gray-900 to-black border border-gray-800 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="p-3 border-b border-gray-800">
                        <p className="text-sm font-medium text-white">{userEmail}</p>
                        <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs ${getRoleBadgeColor(userRole)}`}>
                          <Shield className="w-2.5 h-2.5" />
                          {userRole}
                        </div>
                      </div>
                      <div className="p-1">
                        <button className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                          <Settings className="w-4 h-4" />
                          Settings
                        </button>
                        <button className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-300 hover:text-red-400 hover:bg-white/5 rounded-lg transition-colors" onClick={handleLogout}>
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    variant="ghost"
                    size="icon"
                    className="relative text-gray-300 hover:text-white hover:bg-white/5"
                  >
                    <Bell className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => navigate("/admin-auth")}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 bg-transparent px-6 py-2 rounded-lg"
                >
                  Agency Login
                </Button>
              )}
            </div>

            {/* Mobile Navigation */}
            <div className="flex items-center space-x-2 md:hidden">
              {isLoggedIn && (
                <Button 
                  variant="ghost"
                  size="icon"
                  className="relative text-gray-300 hover:text-white"
                >
                  <Bell className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </Button>
              )}
              
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-300 hover:text-white hover:bg-white/5"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
          <div className="container mx-auto px-6 py-4 space-y-2">
            <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10" asChild onClick={() => setMobileMenuOpen(false)}>
              <Link to="/events">Events</Link>
            </Button>
            <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10" asChild onClick={() => setMobileMenuOpen(false)}>
              <Link to="/pricing">Pricing</Link>
            </Button>

            <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10" asChild onClick={() => setMobileMenuOpen(false)}>
              <Link to="/agency-pricing">Consultation</Link>
            </Button>
            {!isLoggedIn && <Button className="w-full" asChild onClick={() => setMobileMenuOpen(false)}>
              <Link to="/auth">Sign In</Link>
            </Button>}
            {isLoggedIn ? (

              <>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                   <div className="flex items-center gap-3 min-w-0">
                     <Avatar className="h-9 w-9 border border-border"><AvatarImage src={profile?.avatar_url ?? undefined} alt="Your profile picture" /><AvatarFallback><User className="w-4 h-4" /></AvatarFallback></Avatar>
                     <div className="min-w-0">
                    <p className="font-medium text-white">{userEmail}</p>
                    <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs ${getRoleBadgeColor(userRole)}`}>
                      <Shield className="w-2.5 h-2.5" />
                      {userRole}
                    </div>
                     </div>
                  </div>
                </div>
                
                <Link 
                  to="/landing-page-2"
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors px-4 py-3 rounded-lg hover:bg-white/5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span>Dashboard</span>
                </Link>
                
                <button className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors px-4 py-3 rounded-lg hover:bg-white/5 w-full">
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                </button>
                
                <button 
                  className="flex items-center gap-3 text-red-300 hover:text-red-400 transition-colors px-4 py-3 rounded-lg hover:bg-white/5 w-full"
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <Button 
                onClick={() => {
                  navigate("/admin-auth");
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-black font-semibold py-3"
              >
                Agency Login
              </Button>
            )}
            
            <div className="pt-4 border-t border-gray-800">
              <Button 
                variant="ghost"
                onClick={() => {
                  navigate("/");
                  setMobileMenuOpen(false);
                }}
                className="w-full text-gray-400 hover:text-white"
              >
                Return to Main Site
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default AdminHeader;